import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, AlertTriangle, Flame, CloudRain, Zap, Clock } from 'lucide-react'
import { z } from 'zod'
import { Layout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { type Crisis } from '@/lib/types'
import { useReadyPracticeCases } from '@/api/practice-cases'
import { useHasPermission } from '@/lib/hooks/usePermission'

// Validation schema for crisis creation
const crisisFormSchema = z.object({
  type: z.string().min(1, 'crisis.errorTypeRequired').trim(),
  location: z.string().min(1, 'crisis.errorLocationRequired').trim(),
  description: z
    .string()
    .min(1, 'crisis.errorDescriptionRequired')
    .max(5000, 'crisis.errorDescriptionMaxLength')
    .trim(),
  practiceCaseId: z.string().nullable().optional(),
})

type CrisisFormData = z.infer<typeof crisisFormSchema>
type FieldErrors = Partial<Record<keyof CrisisFormData, string>>

const getCrisisIcon = (type: string) => {
  const lowerType = type.toLowerCase()
  if (lowerType.includes('fire')) return Flame
  if (lowerType.includes('flood') || lowerType.includes('water')) return CloudRain
  if (lowerType.includes('power') || lowerType.includes('electric')) return Zap
  return AlertTriangle
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString()
}

export function CrisisSelectPage() {
  const { t } = useTranslation()
  const { createCrisis, selectCrisis, pastCrises, isLoading, loadPastCrises, activeCrisis } =
    useCrisisStore()
  const canCreateCrisis = useHasPermission('crisis.create')
  const [formData, setFormData] = useState({
    type: '',
    location: '',
    description: '',
    practiceCaseId: null as string | null,
  })
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [usePracticeCase, setUsePracticeCase] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  const hasLoadedRef = useRef(false)
  const { data: practiceCasesData } = useReadyPracticeCases()

  useEffect(() => {
    document.title = `Crisis Selecteren - BGCH Crisis Buddy`
  }, [])

  console.debug('[CrisisSelectPage] Render:', {
    pastCrises: pastCrises.length,
    isLoading,
    hasLoaded: hasLoadedRef.current,
    activeCrisis: activeCrisis ? activeCrisis.id : null,
  })

  // If there's already an active crisis, redirect to dashboard
  useEffect(() => {
    if (activeCrisis) {
      console.debug('[CrisisSelectPage] Active crisis detected, redirecting to dashboard')
      window.location.href = '/'
    }
  }, [activeCrisis])

  useEffect(() => {
    if (!hasLoadedRef.current) {
      console.debug('[CrisisSelectPage] useEffect: Loading past crises (first time)')
      hasLoadedRef.current = true
      loadPastCrises()
    }
  }, [loadPastCrises])

  const handleCreateCrisis = async (e: React.FormEvent) => {
    e.preventDefault()
    setFieldErrors({})
    setApiError(null)

    console.debug('[CrisisSelectPage] Creating crisis:', formData)

    try {
      // Validate form data
      const validatedData = crisisFormSchema.parse({
        type: formData.type,
        location: formData.location,
        description: formData.description,
        practiceCaseId: formData.practiceCaseId,
      })

      const crisisData = {
        type: validatedData.type,
        location: validatedData.location,
        description: validatedData.description,
        ...(usePracticeCase && validatedData.practiceCaseId
          ? { practiceCaseId: validatedData.practiceCaseId }
          : {}),
      }
      const crisis = await createCrisis(crisisData)
      console.debug('[CrisisSelectPage] Crisis created:', crisis)
      setFormData({ type: '', location: '', description: '', practiceCaseId: null })
      setUsePracticeCase(false)
      // Redirect to dashboard after creating crisis
      console.debug('[CrisisSelectPage] Redirecting to /')
      window.location.href = '/'
    } catch (validationError) {
      // Handle validation errors
      if (validationError instanceof z.ZodError) {
        console.debug('[CrisisSelectPage] Validation error:', validationError.issues)
        const errors: FieldErrors = {}
        validationError.issues.forEach(issue => {
          const field = issue.path[0] as keyof CrisisFormData | undefined
          if (field) {
            // Get the translated error message
            const message = issue.message.startsWith('crisis.error')
              ? t(issue.message)
              : issue.message
            errors[field] = message
          }
        })
        setFieldErrors(errors)
        return
      }

      // Handle API errors
      console.error('[CrisisSelectPage] Failed to create crisis:', validationError)

      // Parse error message for user-friendly display
      let errorMessage = t('crisis.errorCreateFailed')

      if (validationError instanceof Error) {
        const errorMsg = validationError.message
        // Check for specific error codes
        if (errorMsg.includes('409') || errorMsg.includes('CONFLICT')) {
          errorMessage = t('crisis.errorAlreadyActive')
        } else if (errorMsg.includes('400') || errorMsg.includes('validation')) {
          errorMessage = t('crisis.validationError')
        } else if (errorMsg.includes('401') || errorMsg.includes('unauthorized')) {
          errorMessage = 'U bent niet geautoriseerd om een crisis aan te maken.'
        } else {
          errorMessage = errorMsg
        }
      }

      setApiError(errorMessage)
    }
  }

  const handleSelectCrisis = async (crisis: Crisis) => {
    console.debug('[CrisisSelectPage] Selecting crisis:', crisis.id)
    try {
      await selectCrisis(crisis.id)
      console.debug('[CrisisSelectPage] Crisis selected, redirecting to /')
      // Redirect to dashboard after selecting crisis
      window.location.href = '/dashboard'
    } catch (error) {
      console.error('[CrisisSelectPage] Failed to select crisis:', error)
    }
  }

  return (
    <Layout>
      {/* Hero Section */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-4 py-6 md:px-6 md:py-12">
          <div className="text-center">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground mb-2">
              {t('crisis.selectOrCreate')}
            </h1>
            <p className="text-base md:text-xl text-muted-foreground">
              {t('crisis.selectOrCreateDescription')}
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 md:px-6 md:py-12">
        <div className="max-w-4xl mx-auto">
          <div
            className={`grid gap-4 md:gap-6 ${canCreateCrisis ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}
          >
            {/* Create New Crisis */}
            {canCreateCrisis && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5" />
                    {t('crisis.createNewCrisis')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col min-h-[400px]">
                  <form
                    onSubmit={handleCreateCrisis}
                    className="flex flex-col flex-1 space-y-4"
                    noValidate
                  >
                    {apiError && (
                      <div
                        role="alert"
                        className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md dark:bg-red-950/20 dark:text-red-400 dark:border-red-800"
                      >
                        {apiError}
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="type">{t('crisis.type')}</Label>
                      <Input
                        id="type"
                        value={formData.type}
                        onChange={e => {
                          setFormData(prev => ({ ...prev, type: e.target.value }))
                          // Clear field error when user starts typing
                          if (fieldErrors.type) {
                            setFieldErrors(
                              Object.fromEntries(
                                Object.entries(fieldErrors).filter(([key]) => key !== 'type')
                              )
                            )
                          }
                        }}
                        placeholder={t('crisis.typePlaceholder')}
                        required
                        aria-required="true"
                        aria-invalid={!!fieldErrors.type}
                        aria-describedby={fieldErrors.type ? 'type-error' : undefined}
                      />
                      {fieldErrors.type && (
                        <p id="type-error" className="text-sm text-red-600">
                          {fieldErrors.type}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="location">{t('crisis.location')}</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={e => {
                          setFormData(prev => ({ ...prev, location: e.target.value }))
                          // Clear field error when user starts typing
                          if (fieldErrors.location) {
                            setFieldErrors(
                              Object.fromEntries(
                                Object.entries(fieldErrors).filter(([key]) => key !== 'location')
                              )
                            )
                          }
                        }}
                        placeholder={t('crisis.locationPlaceholder')}
                        required
                        aria-required="true"
                        aria-invalid={!!fieldErrors.location}
                        aria-describedby={fieldErrors.location ? 'location-error' : undefined}
                      />
                      {fieldErrors.location && (
                        <p id="location-error" className="text-sm text-red-600">
                          {fieldErrors.location}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">{t('crisis.description')}</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={e => {
                          setFormData(prev => ({ ...prev, description: e.target.value }))
                          // Clear field error when user starts typing
                          if (fieldErrors.description) {
                            setFieldErrors(
                              Object.fromEntries(
                                Object.entries(fieldErrors).filter(([key]) => key !== 'description')
                              )
                            )
                          }
                        }}
                        placeholder={t('crisis.descriptionPlaceholder')}
                        rows={3}
                        required
                        aria-required="true"
                        aria-invalid={!!fieldErrors.description}
                        aria-describedby={fieldErrors.description ? 'description-error' : undefined}
                      />
                      {fieldErrors.description && (
                        <p id="description-error" className="text-sm text-red-600">
                          {fieldErrors.description}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 pt-4 mt-2 border-t">
                      <Switch
                        id="use-practice-case"
                        checked={usePracticeCase}
                        onCheckedChange={(checked: boolean) => {
                          setUsePracticeCase(checked)
                          if (!checked) {
                            setFormData(prev => ({ ...prev, practiceCaseId: null }))
                          }
                        }}
                        aria-label="Gebruik oefenscenario"
                      />
                      <Label htmlFor="use-practice-case" className="cursor-pointer">
                        Gebruik oefenscenario
                      </Label>
                    </div>

                    {usePracticeCase && (
                      <div className="space-y-2">
                        <Label htmlFor="practice-case">Oefenscenario</Label>
                        <Select
                          value={formData.practiceCaseId || ''}
                          onValueChange={value =>
                            setFormData(prev => ({ ...prev, practiceCaseId: value }))
                          }
                        >
                          <SelectTrigger id="practice-case" aria-label="Selecteer oefenscenario">
                            <SelectValue placeholder="Selecteer een oefenscenario" />
                          </SelectTrigger>
                          <SelectContent>
                            {practiceCasesData?.data?.map(practiceCase => (
                              <SelectItem key={practiceCase.id} value={practiceCase.id}>
                                {practiceCase.title} - {practiceCase.location}
                                {practiceCase.createdAt && (
                                  <span className="text-xs text-muted-foreground ml-2">
                                    ({formatDate(practiceCase.createdAt)})
                                  </span>
                                )}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {practiceCasesData?.data?.length === 0 && (
                          <p className="text-sm text-muted-foreground">
                            Geen oefenscenario's beschikbaar
                          </p>
                        )}
                      </div>
                    )}
                    <Button
                      type="submit"
                      variant="gradient"
                      disabled={isLoading}
                      className="w-full mt-2"
                    >
                      {isLoading ? t('common.creating') : t('crisis.createCrisis')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Past Crises */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  {t('crisis.pastCrises')}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col min-h-[400px]">
                {pastCrises.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    {t('crisis.noPastCrises')}
                  </p>
                ) : (
                  <div className="space-y-3 overflow-y-auto max-h-124">
                    {pastCrises.map(crisis => {
                      const Icon = getCrisisIcon(crisis.type)
                      const isClosed = crisis.status === 'closed'
                      return (
                        <Card
                          key={crisis.id}
                          className="cursor-pointer hover:bg-gray-20 dark:hover:bg-gray-200 transition-colors border-gray-800"
                          onClick={() => handleSelectCrisis(crisis)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handleSelectCrisis(crisis)
                            }
                          }}
                          aria-label={`Selecteer crisis: ${crisis.type} in ${crisis.location}, ${crisis.status === 'active' ? 'actief' : 'gesloten'}`}
                        >
                          <CardContent className="p-3 md:p-4">
                            <div className="flex items-start md:items-center justify-between gap-3">
                              <div className="flex items-start md:items-center space-x-2 md:space-x-3 flex-1 min-w-0">
                                <Icon
                                  className="h-5 w-5 text-black flex-shrink-0 mt-0.5 md:mt-0"
                                  aria-hidden="true"
                                />
                                <div className="min-w-0 flex-1">
                                  <h3 className="font-medium text-sm md:text-base truncate">
                                    {crisis.type}
                                  </h3>
                                  <p className="text-xs md:text-sm text-black-600 dark:text-black-400 truncate">
                                    {crisis.location}
                                  </p>
                                  <p className="text-xs text-black-500">
                                    {formatDate(crisis.createdAt)}
                                  </p>
                                  <div className="flex gap-2 mt-2">
                                    <span
                                      className={`inline-block px-2 py-1 text-xs rounded-full ${
                                        crisis.status === 'active'
                                          ? 'bg-red-200 text-red-800 border-red-800 border-1'
                                          : 'bg-black text-white'
                                      }`}
                                    >
                                      {crisis.status === 'active' ? 'Actief' : 'Gesloten'}
                                    </span>
                                    {crisis.practiceCaseId && (
                                      <span className="inline-block px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                                        Oefening
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={isLoading}
                                className="flex-shrink-0 text-xs md:text-sm"
                                onClick={e => {
                                  e.stopPropagation()
                                  handleSelectCrisis(crisis)
                                }}
                              >
                                {isClosed ? t('crisis.view') : t('crisis.select')}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  )
}
